import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import Logo from '@/components/shared/Logo.tsx';
import authService from '@/services/authService.ts';
import { useAuth } from '@/context/AuthContext.tsx';

type VerifyState = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { isAuthenticated, refreshUser } = useAuth();
  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'error');
  // StrictMode monta dos veces: el segundo intento fallaría porque el token ya
  // se consumió. El ref garantiza una única llamada.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    authService
      .verifyEmail(token)
      .then(async () => {
        if (isAuthenticated) await refreshUser();
        setState('success');
      })
      .catch(() => setState('error'));
  }, [token, isAuthenticated, refreshUser]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-card px-4">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>

          {state === 'verifying' && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="size-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t('auth.verifyEmailVerifying')}</p>
            </div>
          )}

          {state === 'success' && (
            <div className="flex flex-col items-center gap-3">
              <CheckCircle className="size-10 text-green-500" />
              <h1 className="text-lg font-semibold text-foreground">{t('auth.verifyEmailSuccessTitle')}</h1>
              <p className="text-sm text-muted-foreground">{t('auth.verifyEmailSuccessDesc')}</p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover transition-colors no-underline"
              >
                {t('auth.verifyEmailContinue')}
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center gap-3">
              <XCircle className="size-10 text-destructive" />
              <h1 className="text-lg font-semibold text-foreground">{t('auth.verifyEmailErrorTitle')}</h1>
              <p className="text-sm text-muted-foreground">{t('auth.verifyEmailErrorDesc')}</p>
              <Link
                to="/"
                className="mt-4 text-sm font-medium text-primary hover:text-primary-hover transition-colors no-underline"
              >
                {t('auth.verifyEmailBackHome')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
